import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Plus, Minus, Rocket, ShieldCheck, Target, Award, Sparkles, Briefcase } from "lucide-react";
import { INDUSTRIES, MANUFACTURING_ROLES } from "../data";

const ease = [0.16, 1, 0.3, 1];

const industryIcons = {
  it: <Rocket className="w-6 h-6" />,
  healthcare: <ShieldCheck className="w-6 h-6" />,
  defense: <Target className="w-6 h-6" />,
  aerospace: <Award className="w-6 h-6" />,
  "life-sciences": <Sparkles className="w-6 h-6" />,
  manufacturing: <Briefcase className="w-6 h-6" />,
};

export function Industries() {
  const [expanded, setExpanded] = useState("manufacturing");

  return (
    <div data-testid="industries-page" className="page-enter pt-32 sm:pt-40">
      <section className="container-x pb-14">
        <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-4">Industries</p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease }}
          className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-balance max-w-5xl text-[#0F1E3C]"
        >
          Six industries,
          <br />
          <span className="italic font-serif gradient-text-amber">one standard.</span>
        </motion.h1>
        <p className="mt-7 text-lg text-[#475569] max-w-2xl">
          Our recruiters are specialists first, generalists second. Every vertical has a
          dedicated practice lead who&rsquo;s lived in that space for a decade or more.
        </p>
      </section>

      <section className="container-x pb-24">
        <div className="card-soft rounded-3xl overflow-hidden bg-white">
          {INDUSTRIES.map((ind, i) => renderIndustryRow(ind, i, expanded, setExpanded))}
        </div>
      </section>
    </div>
  );
}

function renderIndustryRow(industry, index, expanded, setExpanded) {
  const isExpanded = expanded === industry.id;
  const showRoles = industry.id === "manufacturing";
  return (
    <motion.div
      key={industry.id}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.05, ease }}
      className="border-b border-[#EAE3D2] last:border-b-0"
      data-testid={"industry-row-" + industry.id}
    >
      <button
        onClick={() => setExpanded(isExpanded ? null : industry.id)}
        data-testid={"industry-toggle-" + industry.id}
        className="w-full text-left p-6 sm:p-10 flex items-start justify-between gap-6 hover:bg-[#FAF7F2] transition-colors"
      >
        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
          <div className="md:col-span-2 flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-[#FFF4EA] text-[#E9610E] flex items-center justify-center">
              {industryIcons[industry.id]}
            </span>
            <span className="text-sm font-bold text-[#E9610E]">0{index + 1}</span>
          </div>
          <div className="md:col-span-4">
            <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-[#0F1E3C]">
              {industry.name}
            </h3>
          </div>
          <div className="md:col-span-6">
            <p className="text-base text-[#475569] leading-relaxed">{industry.blurb}</p>
          </div>
        </div>
        <div className="shrink-0 w-10 h-10 rounded-full bg-[#FAF7F2] border border-[#EAE3D2] flex items-center justify-center">
          {isExpanded ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && showRoles && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease }}
            className="overflow-hidden"
          >
            <div className="px-6 sm:px-10 pb-10">
              <p className="text-xs uppercase tracking-[0.2em] text-[#E9610E] font-semibold mb-5">
                Roles we staff across Manufacturing &amp; Engineering
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
                {MANUFACTURING_ROLES.map((role, i) => (
                  <motion.div
                    key={role}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.015 }}
                    className="rounded-full px-4 py-2 text-sm text-[#0F1E3C] text-center bg-[#FAF7F2] border border-[#EAE3D2] hover:border-[#E9610E] hover:bg-[#FFF4EA] transition-colors"
                    data-testid={"role-chip-" + i}
                  >
                    {role}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
